import { useMemo, useState } from 'react';
import { Upload, FileUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { adminApi } from '../../utils/admin-api';
import { API_BASE } from '../../utils/api-base';

interface TagPdfImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string;
  clientId: string;
  plcId: string;
  onImported: () => void;
}

type FieldKey = 'name' | 'address' | 'unit' | 'minValue' | 'maxValue' | 'dataType' | 'description';

const FIELD_LABELS: { key: FieldKey; label: string; required?: boolean }[] = [
  { key: 'name', label: 'Tag Name', required: true },
  { key: 'address', label: 'Address', required: true },
  { key: 'unit', label: 'Unit' },
  { key: 'minValue', label: 'Min Value' },
  { key: 'maxValue', label: 'Max Value' },
  { key: 'dataType', label: 'Data Type' },
  { key: 'description', label: 'Description' },
];

// The `tags` table only accepts a fixed set of data_type values (a DB check
// constraint), but PDF exports use vendor-specific terms — Siemens TIA Portal in
// particular exports "Bool", "Real", "Word", "DWord", "Byte" etc. Map those (and
// common Modbus terms) onto the allowed set instead of passing raw PDF text
// straight into the insert, which the DB would just reject.
const ALLOWED_DATA_TYPES = new Set(['float', 'int', 'bool', 'boolean', 'holding', 'input', 'coil', 'discrete']);
const normalizeDataType = (raw: string): string => {
  const v = raw.trim().toLowerCase();
  if (!v) return 'holding';
  if (ALLOWED_DATA_TYPES.has(v)) return v;
  if (v.includes('bool')) return 'bool';
  if (v.includes('real') || v.includes('float') || v.includes('lreal')) return 'float';
  if (v.includes('int') || v.includes('word') || v.includes('byte') || v.includes('dint') || v.includes('dword')) return 'int';
  return 'holding';
};

const RESPLIT_OPTIONS = [
  { value: 'none', label: 'Auto-detected (default)' },
  { value: 'comma', label: 'Comma ( , )' },
  { value: 'tab', label: 'Tab' },
  { value: 'pipe', label: 'Pipe ( | )' },
  { value: 'space2', label: 'Two or more spaces' },
  { value: 'space1', label: 'Any space (may over-split names)' },
];

export function TagPdfImportDialog({ open, onOpenChange, token, clientId, plcId, onImported }: TagPdfImportDialogProps) {
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [originalRows, setOriginalRows] = useState<string[][]>([]);
  const [included, setIncluded] = useState<boolean[]>([]);
  const [columnMap, setColumnMap] = useState<Partial<Record<FieldKey, number>>>({});
  const [resplit, setResplit] = useState('none');
  const [warning, setWarning] = useState('');
  const [fileName, setFileName] = useState('');

  const reset = () => {
    setParsing(false);
    setImporting(false);
    setOriginalRows([]);
    setIncluded([]);
    setColumnMap({});
    setResplit('none');
    setWarning('');
    setFileName('');
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const rows = useMemo(() => {
    if (resplit === 'none') return originalRows;
    return originalRows.map((row) => {
      const line = row.join(' ');
      if (resplit === 'comma') return line.split(',').map((c) => c.trim()).filter((c) => c.length > 0);
      if (resplit === 'tab') return line.split('\t').map((c) => c.trim()).filter((c) => c.length > 0);
      if (resplit === 'pipe') return line.split('|').map((c) => c.trim()).filter((c) => c.length > 0);
      if (resplit === 'space2') return line.split(/ {2,}/).map((c) => c.trim()).filter((c) => c.length > 0);
      if (resplit === 'space1') return line.split(/\s+/).map((c) => c.trim()).filter((c) => c.length > 0);
      return row;
    });
  }, [originalRows, resplit]);

  const columnCount = useMemo(() => rows.reduce((max, r) => Math.max(max, r.length), 0), [rows]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setWarning('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/${plcId}/tags/parse-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.error || 'Failed to parse PDF');
      const parsedRows: string[][] = payload.rows || [];
      setOriginalRows(parsedRows);
      setIncluded(parsedRows.map((_, i) => i !== payload.headerRowIndex));
      const guess = payload.columnGuess || {};
      const map: Partial<Record<FieldKey, number>> = {};
      (['name', 'address', 'unit', 'minValue', 'maxValue', 'dataType'] as FieldKey[]).forEach((k) => {
        if (typeof guess[k] === 'number') map[k] = guess[k];
      });
      setColumnMap(map);
      setResplit('none');
      if (payload.warning) setWarning(payload.warning);
      else if (parsedRows.length === 0) setWarning('No rows found in this PDF.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to parse PDF');
    } finally {
      setParsing(false);
    }
  };

  const toggleRow = (idx: number) => {
    setIncluded((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const setAllIncluded = (value: boolean) => {
    setIncluded((prev) => prev.map(() => value));
  };

  const selectedCount = included.filter(Boolean).length;
  const canImport = columnMap.name !== undefined && columnMap.address !== undefined && selectedCount > 0;

  const handleImport = async () => {
    if (!canImport) {
      toast.error('Map at least Tag Name and Address, and select at least one row.');
      return;
    }
    const tags = rows
      .map((row, idx) => ({ row, idx }))
      .filter(({ idx }) => included[idx])
      .map(({ row }) => {
        const cell = (key: FieldKey) => {
          const colIdx = columnMap[key];
          return colIdx !== undefined ? (row[colIdx] ?? '').trim() : '';
        };
        const minRaw = cell('minValue');
        const maxRaw = cell('maxValue');
        return {
          name: cell('name'),
          address: cell('address'),
          unit: cell('unit'),
          description: cell('description'),
          dataType: normalizeDataType(cell('dataType')),
          minValue: minRaw !== '' && Number.isFinite(Number(minRaw)) ? Number(minRaw) : 0,
          maxValue: maxRaw !== '' && Number.isFinite(Number(maxRaw)) ? Number(maxRaw) : 100,
        };
      })
      .filter((t) => t.name && t.address);

    if (tags.length === 0) {
      toast.error('No valid rows to import — each row needs both a Tag Name and Address.');
      return;
    }

    setImporting(true);
    try {
      const created = await adminApi.addTags(token, clientId, plcId, tags);
      toast.success(`Imported ${created.length} tag${created.length === 1 ? '' : 's'}`);
      onImported();
      handleClose(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to import tags');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Tags from PDF</DialogTitle>
          <DialogDescription>
            Upload a PDF of your tag list — we'll try to detect the table automatically. Review and map columns
            below before importing; nothing is created until you click Import.
          </DialogDescription>
        </DialogHeader>

        {originalRows.length === 0 ? (
          <div className="border-2 border-dashed rounded-lg p-10 text-center space-y-3">
            <FileUp className="h-8 w-8 mx-auto text-muted-foreground" />
            <div>
              <label className="cursor-pointer">
                <span className="text-primary font-medium hover:underline">Choose a PDF file</span>
                <input type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} disabled={parsing} />
              </label>
              {parsing && (
                <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Parsing PDF...
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{fileName} — {rows.length} row(s) detected, {selectedCount} selected</span>
              <Button variant="ghost" size="sm" onClick={reset}>Upload a different file</Button>
            </div>

            {warning && (
              <div className="text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-300 rounded-md p-3">
                {warning}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Re-split rows by</Label>
                <Select value={resplit} onValueChange={(v) => { setResplit(v); setColumnMap({}); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESPLIT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-4">
              {FIELD_LABELS.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs">{f.label}{f.required ? ' *' : ''}</Label>
                  <Select
                    value={columnMap[f.key] !== undefined ? String(columnMap[f.key]) : '__none__'}
                    onValueChange={(v) => setColumnMap((prev) => ({ ...prev, [f.key]: v === '__none__' ? undefined : Number(v) }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Not mapped —</SelectItem>
                      {Array.from({ length: columnCount }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>Column {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setAllIncluded(true)}>Select all</Button>
              <Button variant="outline" size="sm" onClick={() => setAllIncluded(false)}>Deselect all</Button>
            </div>

            <div className="border rounded-lg overflow-auto max-h-80">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-2 text-left w-8"></th>
                    <th className="p-2 text-left">#</th>
                    {Array.from({ length: columnCount }, (_, i) => (
                      <th key={i} className="p-2 text-left whitespace-nowrap">Col {i + 1}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} className={`border-t ${included[idx] ? '' : 'opacity-40'}`}>
                      <td className="p-2">
                        <Checkbox checked={!!included[idx]} onCheckedChange={() => toggleRow(idx)} />
                      </td>
                      <td className="p-2 text-muted-foreground">{idx + 1}</td>
                      {Array.from({ length: columnCount }, (_, c) => (
                        <td key={c} className="p-2 whitespace-nowrap">{row[c] ?? ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
          {originalRows.length > 0 && (
            <Button onClick={handleImport} disabled={!canImport || importing}>
              {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {importing ? 'Importing...' : `Import ${selectedCount} Tag${selectedCount === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
